import React, { useEffect, useState } from 'react';
import { Table, message } from 'antd';
import { businessService, Business, BusinessDirector, BusinessMember } from '../../services/businesses';
import customerApi from '../../services/customerApi';

interface BusinessTabProps {
  customerId?: number;
  isCustomerPortal?: boolean;
}

export const BusinessTab: React.FC<BusinessTabProps> = ({ customerId, isCustomerPortal = false }) => {
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (customerId || isCustomerPortal) {
      loadBusinesses();
    }
  }, [customerId, isCustomerPortal]);

  const loadBusinesses = async () => {
    setLoading(true);
    try {
      if (isCustomerPortal) {
        // Use customer API for customer portal
        const response = await customerApi.get('/customer/businesses');
        setBusinesses(response.data.data || []);
      } else if (customerId) {
        // Use admin API for admin portal
        const response = await businessService.getAll();
        // Filter businesses by directors/members customer_id
        const customerBusinesses = (response.data || []).filter((b: Business) =>
          (b.directors || []).some((director) => director.customer_id === customerId) ||
          (b.members || []).some((member) => member.customer_id === customerId)
        );
        setBusinesses(customerBusinesses);
      }
    } catch {
      message.error('Failed to load businesses');
    } finally {
      setLoading(false);
    }
  };

  // Combine all directors and members from all businesses
  const allDirectors: (BusinessDirector & { businessName: string })[] = [];
  const allMembers: (BusinessMember & { businessName: string })[] = [];

  businesses.forEach((business) => {
    if (business.directors) {
      business.directors
        .filter((director) => (customerId ? director.customer_id === customerId : true))
        .forEach((director) => {
          allDirectors.push({ ...director, businessName: business.name });
        });
    }
    if (business.members) {
      business.members
        .filter((member) => (customerId ? member.customer_id === customerId : true))
        .forEach((member) => {
          allMembers.push({ ...member, businessName: business.name });
        });
    }
  });

  const directorColumns = [
    {
      title: 'Business',
      dataIndex: 'businessName',
      key: 'businessName',
      render: (text: string) => text || '-',
    },
    {
      title: 'Position',
      dataIndex: 'position',
      key: 'position',
      render: (text: string) => text || '-',
    },
    {
      title: 'Appoint Date',
      dataIndex: 'appoint_date',
      key: 'appoint_date',
      render: (date: string) => date ? new Date(date).toLocaleDateString() : '-',
    },
    {
      title: 'Resign Date',
      dataIndex: 'resign_date',
      key: 'resign_date',
      render: (date: string) => date ? new Date(date).toLocaleDateString() : '-',
    },
  ];

  const memberColumns = [
    {
      title: 'Business',
      dataIndex: 'businessName',
      key: 'businessName',
      render: (text: string) => text || '-',
    },
    {
      title: 'Share',
      dataIndex: 'share',
      key: 'share',
      render: (share: number) => share ? `${share}%` : '-',
    },
    {
      title: 'Appoint Date',
      dataIndex: 'appoint_date',
      key: 'appoint_date',
      render: (date: string) => date ? new Date(date).toLocaleDateString() : '-',
    },
    {
      title: 'Resign Date',
      dataIndex: 'resign_date',
      key: 'resign_date',
      render: (date: string) => date ? new Date(date).toLocaleDateString() : '-',
    },
  ];

  return (
    <div>
      <Table
        columns={directorColumns}
        dataSource={allDirectors.map((d, index) => ({ ...d, key: `director-${index}` }))}
        pagination={false}
        size="small"
        loading={loading}
      />

      <Table
        style={{ marginTop: 16 }}
        columns={memberColumns}
        dataSource={allMembers.map((m, index) => ({ ...m, key: `member-${index}` }))}
        pagination={false}
        size="small"
        loading={loading}
      />
    </div>
  );
};
